import { CartItem, Currency } from "../types/checkoutTypes"
import { getCartProduct } from "../../utils/stripejs"
import { CartProduct } from "../../ts/interfaces"
import type { Product } from "../product"
import { SKU_CONFIG } from "../skuConfig"
import { isHeadset, isHeadsetInCart } from "./headsetUtils"
export { isHeadset, isHeadsetInCart }

export const calcCartSubtotal = (
  cartItems: CartItem[],
  currency: Currency,
  trialDays: number
): number => {
  return getCartItemsForTotals(cartItems, trialDays)
    .map(item => {
      const product = getCartProduct(item.productId)
      return getPrice(product, item.quantity, currency)
    })
    .reduce((acc, price) => acc + price, 0)
}

const getPrice = (
  product: CartProduct | null,
  quantity: number,
  currency: Currency
): number => {
  if (!product || !product.prices || !product.prices[currency]) {
    return 0
  }
  if (product.prices[currency].recurring?.usage_type === "metered") {
    return 0
  }
  let cyclesUpfront = 1
  if (product.metadata.NUM_PERIOD_UPFRONT) {
    cyclesUpfront = parseInt(product.metadata.NUM_PERIOD_UPFRONT, 10)
  }

  return product.prices[currency]!.unit_amount * quantity * cyclesUpfront
}

export const calcCartTotal = (
  cartItems: CartItem[],
  currency: Currency,
  trialDays: number,
  taxes: number = 0,
  shipping: number = 0,
  couponDiscount: number = 0
): number => {
  return (
    getCartItemsForTotals(cartItems, trialDays)
      .map(item => {
        const product = getCartProduct(item.productId)
        const pr = getPrice(product, item.quantity, currency)

        return pr
      })
      .reduce((acc, price) => acc + price, 0) +
    taxes +
    shipping -
    couponDiscount
  )
}

export const isTaxableItemInCart = (cartItems: CartItem[]): boolean => {
  return cartItems
    .map(item => {
      const product = getCartProduct(item.productId)
      return product &&
        product.metadata.avataxCode &&
        product.metadata.avataxCode !== "false"
        ? true
        : false
    })
    .includes(true)
}

export const isShippableItemInCart = (cartItems: CartItem[]): boolean => {
  return cartItems
    .map(item => {
      const product = getCartProduct(item.productId)

      return product !== null && product.metadata.shippingRequired === "true"
    })
    .includes(true)
}

export const isContractItemInCart = (
  cartItems: CartItem[],
  products: Product[]
): boolean => {
  return cartItems.some(item => {
    const p = products.find(sp => {
      return item.productId === sp.actualstripeId
    })
    return p && p.isAnnualContract()
  })
}

export const getContractItemInCart = (
  cartItems: CartItem[],
  products: Product[]
): Product | null => {
  let result: Product | null = null
  cartItems.forEach(item => {
    const p = products.find(sp => {
      return item.productId === sp.actualstripeId
    })
    if (p && p.isAnnualContract()) {
      result = p
    }
  })
  return result
}

export const getShippableCartItemIds = (cartItems: CartItem[]): string[] => {
  const ids = []

  for (const item of cartItems) {
    const product = getCartProduct(item.productId)
    if (product !== null && product.metadata.shippingRequired === "true") {
      ids.push(item.productId)
    }
  }
  return ids
}

export const isSubscriptionInCart = (cartItems: CartItem[]): boolean => {
  return cartItems.some(item => {
    const product = getCartProduct(item.productId)

    return (
      product !== null &&
      product !== undefined &&
      product.prices.cad?.recurring?.usage_type !== "metered" &&
      (product.prices.cad?.type === "recurring" ||
        product.prices.usd?.type === "recurring")
    )
  })
}

export const isItemInCart = (
  checkItem: CartProduct,
  cartItems: CartItem[]
): boolean => {
  return cartItems.some(item => {
    const product = getCartProduct(item.productId)

    return product?.id === checkItem.id
  })
}

export const isPersonalSubscriptionInCart = (
  cartItems: CartItem[]
): boolean => {
  return cartItems.some(item => {
    const product = getCartProduct(item.productId)
    const sku = product?.metadata.SKU ?? ""
    return SKU_CONFIG[sku]?.isPersonal === true
  })
}

export const isAccountRequiredItemInCart = (cartItems: CartItem[]): boolean => {
  return cartItems
    .map(item => {
      const product = getCartProduct(item.productId)

      return product !== null && product.metadata?.accountRequired === "true"
    })
    .includes(true)
}

export const calcNumItemsInCart = (cartItems: CartItem[]): number => {
  return cartItems
    .map(item => {
      const product = getCartProduct(item.productId)
      if (product?.prices.cad?.recurring?.usage_type === "metered") {
        return 0
      }

      if (!!product?.metadata.trial_days && isHeadsetInCart(cartItems)) {
        return 0
      }

      return item.quantity
    })
    .reduce((acc, quantity) => acc + quantity, 0)
}

export const doesPurchaseRequireAccount = (cartItems: CartItem[]): boolean =>
  isSubscriptionInCart(cartItems) || isAccountRequiredItemInCart(cartItems)

// TODO: Confirm headset not just "shippable item"
export const isHeadsetAndMembershipInCart = (cartItems: CartItem[]) =>
  isSubscriptionInCart(cartItems) && isHeadsetInCart(cartItems)

const getSubscriptionTrialDays = (product: CartProduct): number => {
  if (!product?.metadata?.trial_days) return 0
  return parseInt(product.metadata.trial_days)
}

export const getSubscriptionInCartTrialDays = (
  cartItems: CartItem[]
): number => {
  if (getNonShippableItemsInCart(cartItems).length === 0) return 0

  let trialDays = 0

  const items = getNonShippableItemsInCart(cartItems)
  items.forEach(item => {
    const product = getCartProduct(item.productId)

    if (product) {
      const days = getSubscriptionTrialDays(product)
      trialDays = trialDays < days ? days : trialDays
    }
  })

  return trialDays
}

export const isHeadsetSubscriptionPromoApplicable = (cartItems: CartItem[]) => {
  return (
    isHeadsetAndMembershipInCart(cartItems) &&
    isSubscriptionWithTrialInCart(cartItems)
  )
}

const getNonShippableItemsInCart = (cartItems: CartItem[]): CartItem[] => {
  return cartItems.filter(item => {
    const product = getCartProduct(item.productId)

    return product !== null && product.metadata.shippingRequired !== "true"
  })
}

export const getShippableItemsInCart = (cartItems: CartItem[]): CartItem[] => {
  return cartItems.filter(item => {
    const product = getCartProduct(item.productId)

    return product !== null && product.metadata.shippingRequired === "true"
  })
}

const isSubscriptionWithTrialInCart = (cartItems: CartItem[]) =>
  cartItems
    .map(item => {
      const product = getCartProduct(item.productId)

      return (
        product !== null &&
        (product.prices.cad?.type === "recurring" ||
          product.prices.usd?.type === "recurring") &&
        getSubscriptionTrialDays(product) > 0
      )
    })
    .includes(true)

/**
 * Trial days from any source that means the subscription is not billed today - a coupon
 * trial or a membership trial grant. The server resolves the same way: every trial
 * source competes and the longest wins (SubscriptionService.getSubscriptionTrialDays).
 * Despite the field name, the grant value is the longer of the user's two grant slots.
 */
export const getChargeSuppressingTrialDays = (trials: {
  couponDiscountDays: number
  grantsLinkedUserMembershipTrialDays: number
}): number =>
  Math.max(
    trials.couponDiscountDays,
    trials.grantsLinkedUserMembershipTrialDays
  )

// When a trial applies the subscription is not charged today, so it drops out of the
// totals and only the shippable items are billed.
export const getCartItemsForTotals = (
  cartItems: CartItem[],
  trialDays: number
) => {
  if (isHeadsetSubscriptionPromoApplicable(cartItems) || trialDays > 0) {
    return getShippableItemsInCart(cartItems)
  }
  return cartItems
}

export const getOutofRegionProducts = (
  country: string,
  cartItems: CartItem[],
  products: Product[]
): Product[] => {
  const outOfRegionP: Product[] = []
  cartItems.forEach(ci => {
    const p = products.find(sp => {
      return sp.isOutOfRegion(country) && ci.productId === sp.actualstripeId
    })
    if (p) {
      outOfRegionP.push(p)
    }
  })
  return outOfRegionP
}
